// useAgentConfig — the signed-in user's trading guardrail settings
// (agent_config) from /api/agent-config, plus today's usage tally.
//
// Shaped after useLivePrices/useAgentPortfolio (SURFACES error). Returns
// { config, usage, loading, error, saving, save, killSwitch, refresh }:
//   - config: the settings object (server defaults when unset) or null while
//     loading; usage: { traded_dollars, trade_count } for the daily meter
//   - save(partial): POSTs only the provided fields; resolves the saved row
//     (throws on failure so the panel can show the error + keep edits)
//   - killSwitch(on): fast path — immediate single-field save with optimistic
//     local flip so the strip reacts instantly, rolled back on failure
//
// Auth: Clerk session token (writes are session-only server-side; agents
// read this endpoint with their API key, never through this hook).

import { useCallback, useEffect, useState } from 'react'
import { useAuth, useUser } from '@clerk/clerk-react'

export function useAgentConfig() {
  const { user, isLoaded } = useUser()
  const { getToken } = useAuth()
  const [config, setConfig] = useState(null)
  const [usage, setUsage] = useState({ traded_dollars: null, trade_count: null })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!isLoaded) return
    if (!user?.id) { setConfig(null); setLoading(false); setError(null); return }
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const token = await getToken()
        if (cancelled) return
        const r = await fetch('/api/agent-config', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        const j = await r.json().catch(() => ({}))
        if (cancelled) return
        if (!r.ok) {
          setError(new Error(j?.error || `agent-config HTTP ${r.status}`))
          setLoading(false)
          return
        }
        setConfig(j.config ?? null)
        setUsage(j.usage ?? { traded_dollars: null, trade_count: null })
        setError(null)
        setLoading(false)
      } catch (e) {
        if (!cancelled) { setError(e); setLoading(false) }
      }
    })()
    return () => { cancelled = true }
    // getToken stable per Clerk session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, user?.id, tick])

  const post = useCallback(async (partial) => {
    const token = await getToken()
    const r = await fetch('/api/agent-config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(partial),
    })
    const j = await r.json().catch(() => ({}))
    if (!r.ok) throw new Error(j?.error || `agent-config HTTP ${r.status}`)
    return j.config
  }, [getToken])

  const save = useCallback(async (partial) => {
    setSaving(true)
    try {
      const saved = await post(partial)
      setConfig(saved)
      return saved
    } finally {
      setSaving(false)
    }
  }, [post])

  const killSwitch = useCallback(async (on) => {
    const prev = config
    setConfig((c) => (c ? { ...c, kill_switch: on } : c))   // optimistic — the strip must react NOW
    try {
      const saved = await post({ kill_switch: on })
      setConfig(saved)
      return saved
    } catch (e) {
      setConfig(prev)   // roll back the optimistic flip
      throw e
    }
  }, [post, config])

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  return { config, usage, loading, saving, error, save, killSwitch, refresh, signedIn: !!user?.id }
}
